import { memo } from "react";

import styles from "./RebateProgressItem.module.css";
import {
    useSelectedCampaign,
    type ICashbackLevel,
    type IUserProgress
} from "features/campaigns/_exports";
import { getLevelFillPercent, isCurrentLevel, isGateUnlocked } from "../../../../utils/variants/rebateHelpers";
import { useCurrency } from "store/app/appStore";
import { useClampTooltipToCard } from "./useClampTooltipToCard";

interface RebateProgressItemProps {
    cashbackLevel: ICashbackLevel
    userProgress: IUserProgress | null
    minWinBetAmount: number
    variant: 'card' | 'winnings-tab'
    forceFull?: boolean
    isHighestLevel?: boolean
}

const FULL = 100;

interface TotalWinTooltipProps {
    userProgress: IUserProgress | null
    isLevelUnlocked: boolean
    isHighestLevel: boolean
}

const getTotalWinAmount = (userProgress: IUserProgress | null, isGateUnlocked: boolean): number => {
    if (isGateUnlocked && userProgress && userProgress.totalWinAmount) return userProgress.totalWinAmount;
    return 0; // if gate is locked or there is no user progress show 0
};

const Tooltip: React.FC<TotalWinTooltipProps> = ({ userProgress, isLevelUnlocked, isHighestLevel }) => {
    const selectedCampaign = useSelectedCampaign();
    const currency = useCurrency();
    const camapaignCurrency = selectedCampaign?.baseCurrency || currency;
    const cardOverflowIsPossible = isHighestLevel;
    const tooltipRef = useClampTooltipToCard(cardOverflowIsPossible);

    return (
        <span ref={tooltipRef} className={styles.totalWinTooltip} data-status={isLevelUnlocked ? 'unlocked': 'locked'}>
            <span>
                {getTotalWinAmount(userProgress, isLevelUnlocked)}<span className={styles.totalWinTooltipCurrency}>{camapaignCurrency}</span>
            </span>
        </span>
    );
};

const RebateProgressItem: React.FC<RebateProgressItemProps> = ({ cashbackLevel, variant, userProgress, minWinBetAmount, forceFull, isHighestLevel }) => {
    const fillPercentage = forceFull ? FULL : getLevelFillPercent(userProgress, cashbackLevel, minWinBetAmount);
    const spentAmount = userProgress?.spentBetAmount;
    const isLevelUnlocked = isGateUnlocked(spentAmount, minWinBetAmount);
    const isCurrent = isCurrentLevel(spentAmount, cashbackLevel);
    const showTotalWinTooltip = variant === 'card' && isCurrent;

    return (
        <div className={styles.progressItemContainer} data-variant={variant}>
            {
                fillPercentage > 0 && (
                    <div
                        className={styles.progressItemFill}
                        data-percent={fillPercentage}
                        data-is-unlocked={isLevelUnlocked}
                        data-is-current={isCurrent}
                        style={{ width: `calc(${fillPercentage}% + 2 * var(--progress-item-border))` }}
                    >
                        {showTotalWinTooltip &&
                            <Tooltip
                                userProgress={userProgress}
                                isLevelUnlocked={isLevelUnlocked}
                                isHighestLevel={!!isHighestLevel}
                            />
                        }
                    </div>
                )
            }
        </div >
    );
};

export default memo(RebateProgressItem);